/**
 * Dev-only tracking mocks (two modes — only one is active at a time).
 *
 * VITE_MOCK_TRACKING_CLIENT=1
 *   Customer map only: fake route pickup → delivery in the browser.
 *   Ignores API/SSE driverLocation (no override flag needed).
 *
 * VITE_MOCK_TRACKING_SSE=1
 *   Provider app: PATCH scripted GPS; customers receive driverLocation via SSE/poll.
 *   Route from VITE_MOCK_TRACKING_SSE_ORIGIN_* or VITE_MOCK_TRACKING_SSE_ROUTE.
 *
 * Precedence: CLIENT wins. If both are 1, SSE driver mock is disabled so locations
 * do not fight and the map does not shake on a stale real fix.
 *
 * Client mock speed: VITE_MOCK_TRACKING_CLIENT_STEP (route fraction per tick),
 * VITE_MOCK_TRACKING_CLIENT_INTERVAL_MS (tick rate), VITE_MOCK_TRACKING_CLIENT_ANIMATION_MS (map ease).
 */

function envOn(val: string | undefined): boolean {
  const v = val?.trim();
  return v === "1" || v === "true";
}

export const MOCK_TRACKING_CLIENT = envOn(import.meta.env.VITE_MOCK_TRACKING_CLIENT);

export const MOCK_TRACKING_SSE = envOn(import.meta.env.VITE_MOCK_TRACKING_SSE);

/** Customer OrderTrackingMap — in-browser mock only. */
export const USE_MOCK_TRACKING_CLIENT = MOCK_TRACKING_CLIENT;

/** Provider useDriverSessionLocation — SSE mock when client mock is off. */
export const USE_MOCK_TRACKING_SSE_DRIVER =
  MOCK_TRACKING_SSE && !MOCK_TRACKING_CLIENT;

/** Client mock: fraction of pickup→delivery per tick (0.04 ≈ 25 ticks for full route). */
export const MOCK_TRACKING_CLIENT_STEP = parseEnvFloat(
  import.meta.env.VITE_MOCK_TRACKING_CLIENT_STEP,
  0.02,
);

/** Client mock: ms between position updates. Lower = faster updates. */
export const MOCK_TRACKING_CLIENT_INTERVAL_MS = parseEnvInt(
  import.meta.env.VITE_MOCK_TRACKING_CLIENT_INTERVAL_MS,
  1000,
);

/** Client mock: ms to ease between each update on the map. Lower = snappier marker. */
export const MOCK_TRACKING_CLIENT_ANIMATION_MS = parseEnvInt(
  import.meta.env.VITE_MOCK_TRACKING_CLIENT_ANIMATION_MS,
  800,
);

function parseEnvFloat(val: string | undefined, fallback: number): number {
  const n = parseFloat(val?.trim() ?? "");
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function parseEnvInt(val: string | undefined, fallback: number): number {
  const n = parseInt(val?.trim() ?? "", 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}
